import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { skipToken } from '@apollo/client/react';
import { css, Global } from '@emotion/react';
import { Col, Row } from '@opendoor/bricks-next';
import { useLocation } from '@remix-run/react';
import { useMachine } from '@xstate/react';
import { DownloadApp } from 'buying/components/showingRequestCreation/DownloadApp';
import { Header } from 'buying/components/showingRequestCreation/Header';
import { PropertyConfirmation } from 'buying/components/showingRequestCreation/PropertyConfirmation';
import type { ConfirmationProperty } from 'buying/components/showingRequestCreation/PropertyConfirmation';
import { ShowingRequestPropertyDocument } from 'buying/graphql/queries/__generated__/ShowingRequestProperty';
import { ShowingRequestCreationStateMachine } from 'buying/stateMachines/showingRequestCreationStateMachine';
import { useQuery } from 'platform/apollo';
import { LoadingPage } from 'platform/components/LoadingPage';
import { PageState, useAutoTrackPageViews } from 'platform/hooks/useAutoTrackPageViews';
import navigationHelpers from 'platform/navigationHelpers';
import { HOMEPAGE_URL } from 'shared/helpers/globals';
import { NotFoundPage } from 'shared/routes/errors/NotFoundPage';

const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';

/**
 * Showing request creation flow
 */
export const ShowingRequestCreation: FC = () => {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const addressId = queryParams.get('id');
  const { data, loading, error } = useQuery(
    ShowingRequestPropertyDocument,
    !addressId
      ? skipToken
      : {
          variables: {
            addressId,
          },
        },
  );
  const property = data?.marketplace?.property;
  const propertyPhoto = `${property?.photos?.[0]}?preset=3%3A2-sm&service=buyer`;

  const [state, send] = useMachine(ShowingRequestCreationStateMachine);
  const { value } = state;

  useAutoTrackPageViews(loading ? PageState.LOADING : PageState.LOADED);

  useEffect(() => {
    if (value === 'IDLE' && property) {
      send({ type: 'setAddressId', value: addressId });
      send({ type: 'setProperty', value: property });
    }
  }, [addressId, property, value, send]);

  useEffect(() => {
    if (value === 'IDLE' && !loading) {
      send('FORWARD');
    }
  }, [value, loading, send]);

  const handleOnNext = () => {
    send('FORWARD');
  };

  /**
   * For terminal states, redirect to PDP
   */
  if (state.value === 'REDIRECT') {
    navigationHelpers.hardNavAssign(
      `${HOMEPAGE_URL}/properties/aid_${state.context.property?.addressId}`,
    );
  } else if (state.value === 'APP_REDIRECT') {
    navigationHelpers.hardNavAssign(APP_DOWNLOAD_URL);
  }

  let activeStep = null;
  switch (state.value) {
    case 'PROPERTY_CONFIRM':
      activeStep = (
        <PropertyConfirmation
          handleOnNext={handleOnNext}
          property={property as ConfirmationProperty}
        />
      );
      break;
    case 'DOWNLOAD_APP':
      activeStep = <DownloadApp handleOnNext={handleOnNext} />;
      break;
    default:
      activeStep = null;
      break;
  }

  if (!addressId || error) {
    return <NotFoundPage />;
  }

  if (loading || value === 'REDIRECT' || value === 'APP_REDIRECT') {
    return <LoadingPage />;
  }

  const showHeader = value !== 'PROPERTY_CONFIRM';

  return (
    <>
      <Col alignItems="center" $largerThanSM={{ px: '$16x' }} m="$8x">
        <Header
          bedrooms={property?.bedrooms}
          bathrooms={property?.bathroomsTotal}
          sqft={property?.totalLivingSqFt}
          photo={propertyPhoto}
          show={showHeader}
          street={property?.street}
        />
        <Row pt="$8x" mx="$8x" justifyContent="center">
          {activeStep}
        </Row>
      </Col>
      <Global
        styles={css`
          body {
            background-color: #fff;
            height: auto;
          }
        `}
      />
    </>
  );
};
